import { sortSalaryRevisions } from "./salaryRevisions";
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import * as XLSX from "xlsx-js-style";
import { cmpEmpId, cmpEmpName, getEmpIdCode } from "./salaryCalculator";

// ─────────────────────────────────────────────────────────────────────────────
// ── HELPER: EXCEL AUTO-WIDTH & STYLING
// ─────────────────────────────────────────────────────────────────────────────
function exportStyledWorkbook(sheets, filename) {
  const wb = XLSX.utils.book_new();

  sheets.forEach(({ name, data, title, subtitle }) => {
    if (!data || data.length === 0) return;

    // Header styling
    const headerStyle = {
      font: { name: "Arial", sz: 11, bold: true, color: { rgb: "0F172A" } },
      fill: { fgColor: { rgb: "E2E8F0" } }, // Light gray gradient tone
      alignment: { vertical: "center", horizontal: "center", wrapText: true },
      border: {
        top: { style: "thin", color: { rgb: "CBD5E1" } },
        bottom: { style: "medium", color: { rgb: "94A3B8" } },
        left: { style: "thin", color: { rgb: "CBD5E1" } },
        right: { style: "thin", color: { rgb: "CBD5E1" } },
      },
    };

    const cellStyle = {
      font: { name: "Arial", sz: 10, color: { rgb: "1E293B" } },
      alignment: { vertical: "center", horizontal: "left" },
      border: {
        top: { style: "thin", color: { rgb: "F1F5F9" } },
        bottom: { style: "thin", color: { rgb: "E2E8F0" } },
        left: { style: "thin", color: { rgb: "F1F5F9" } },
        right: { style: "thin", color: { rgb: "F1F5F9" } },
      },
    };

    const numCellStyle = {
      ...cellStyle,
      alignment: { vertical: "center", horizontal: "right" },
    };

    // Build worksheet with Title, Subtitle, and Data
    const aoa = [];
    if (title) aoa.push([title]);
    if (subtitle) aoa.push([subtitle]);
    if (title || subtitle) aoa.push([]); // blank spacing row

    const headers = Object.keys(data[0] || {});
    aoa.push(headers);

    data.forEach(row => {
      aoa.push(headers.map(h => row[h] !== undefined && row[h] !== null ? row[h] : ""));
    });

    const ws = XLSX.utils.aoa_to_sheet(aoa);

    // Apply auto column widths
    const colWidths = headers.map((h, i) => {
      let maxLen = h.length;
      data.forEach(row => {
        const val = row[h];
        if (val !== undefined && val !== null) {
          const s = String(val);
          if (s.length > maxLen) maxLen = s.length;
        }
      });
      return { wch: Math.min(42, Math.max(12, maxLen + 3)) };
    });
    ws["!cols"] = colWidths;

    // Apply styles to cells
    const headerRowIdx = (title || subtitle) ? ((title && subtitle) ? 3 : 2) : 0;
    const range = XLSX.utils.decode_range(ws["!ref"] || "A1:A1");

    for (let R = range.s.r; R <= range.e.r; ++R) {
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cellRef = XLSX.utils.encode_cell({ r: R, c: C });
        if (!ws[cellRef]) continue;

        // Title row styling
        if (title && R === 0) {
          ws[cellRef].s = {
            font: { name: "Arial", sz: 14, bold: true, color: { rgb: "0F172A" } },
            alignment: { vertical: "center", horizontal: "left" },
          };
        } else if (subtitle && R === 1) {
          ws[cellRef].s = {
            font: { name: "Arial", sz: 10, italic: true, color: { rgb: "64748B" } },
            alignment: { vertical: "center", horizontal: "left" },
          };
        } else if (R === headerRowIdx) {
          ws[cellRef].s = headerStyle;
        } else if (R > headerRowIdx) {
          const val = ws[cellRef].v;
          const isNum = typeof val === "number" || (typeof val === "string" && /^₹?[\d,.]+%?$/.test(val.trim()));
          ws[cellRef].s = isNum ? numCellStyle : cellStyle;
        }
      }
    }

    XLSX.utils.book_append_sheet(wb, ws, name.slice(0, 31));
  });

  XLSX.writeFile(wb, `${filename.replace(/\s+/g, "_")}.xlsx`);
}

// ─────────────────────────────────────────────────────────────────────────────
// ── EMPLOYEE MASTER COLUMN DEFINITIONS
// ─────────────────────────────────────────────────────────────────────────────
const EMPLOYEE_COLUMNS = [
  // Mandatory
  { key: "employee_code", label: "Employee ID", mandatory: true, category: "Work" },
  { key: "name", label: "Full Name", mandatory: true, category: "Work" },
  // Work
  { key: "department", label: "Department", mandatory: false, category: "Work", default: true },
  { key: "designation", label: "Designation", mandatory: false, category: "Work", default: true },
  { key: "date_of_joining", label: "Date of Joining", mandatory: false, category: "Work", default: true },
  { key: "status", label: "Status", mandatory: false, category: "Work", default: true },
  { key: "employment_type", label: "Employment Type", mandatory: false, category: "Work", default: true },
  // Contact
  { key: "email", label: "Official Email", mandatory: false, category: "Contact", default: true },
  { key: "phone", label: "Mobile Phone", mandatory: false, category: "Contact", default: true },
  { key: "emergency_contact", label: "Emergency Contact", mandatory: false, category: "Contact", default: false },
  { key: "current_address", label: "Current Address", mandatory: false, category: "Contact", default: false },
  { key: "permanent_address", label: "Permanent Address", mandatory: false, category: "Contact", default: false },
  // Salary
  { key: "base_salary", label: "Base Salary (₹)", mandatory: false, category: "Salary", default: true },
  { key: "monthly_gross", label: "Gross Salary (₹)", mandatory: false, category: "Salary", default: true },
  { key: "ctc", label: "Annual CTC (₹)", mandatory: false, category: "Salary", default: true },
  { key: "payment_mode", label: "Payment Mode", mandatory: false, category: "Salary", default: false },
  // Statutory / KYC
  { key: "bank_name", label: "Bank Name", mandatory: false, category: "Statutory", default: true },
  { key: "account_number", label: "Account Number", mandatory: false, category: "Statutory", default: true },
  { key: "ifsc_code", label: "IFSC Code", mandatory: false, category: "Statutory", default: true },
  { key: "pan_number", label: "PAN Number", mandatory: false, category: "Statutory", default: true },
  { key: "aadhaar_number", label: "Aadhaar Number", mandatory: false, category: "Statutory", default: true },
  { key: "uan_number", label: "UAN Number", mandatory: false, category: "Statutory", default: false },
  { key: "pf_number", label: "PF Number", mandatory: false, category: "Statutory", default: false },
  { key: "esi_number", label: "ESI Number", mandatory: false, category: "Statutory", default: false },
  // Personal
  { key: "gender", label: "Gender", mandatory: false, category: "Personal", default: false },
  { key: "date_of_birth", label: "Date of Birth", mandatory: false, category: "Personal", default: false },
  { key: "marital_status", label: "Marital Status", mandatory: false, category: "Personal", default: false },
  { key: "blood_group", label: "Blood Group", mandatory: false, category: "Personal", default: false },
];

// ─────────────────────────────────────────────────────────────────────────────
// ── SALARY HIKE COLUMN DEFINITIONS
// ─────────────────────────────────────────────────────────────────────────────
const SALARY_HIKE_COLUMNS = [
  { key: "employee_code", label: "Employee ID", mandatory: true },
  { key: "name", label: "Employee Name", mandatory: true },
  { key: "department", label: "Department", mandatory: false, default: true },
  { key: "old_base", label: "Previous Base (₹)", mandatory: true },
  { key: "new_base", label: "Revised Base (₹)", mandatory: true },
  { key: "hike_amount", label: "Hike Amount (₹)", mandatory: true },
  { key: "hike_percent", label: "Hike %", mandatory: true },
  { key: "effective_date", label: "Effective Date", mandatory: true },
  { key: "reason", label: "Appraisal Cycle / Reason", mandatory: false, default: true },
  { key: "approved_by", label: "Approved By", mandatory: false, default: true },
  { key: "created_at", label: "Logged Date", mandatory: false, default: false },
];

export default function AdminReportsHub({
  company,
  employees = [],
  currentUser,
  isCompanyRole,
  supabase,
  getEmployeeLeaveMetrics,
  salaries = {},
  onBack,
  onDownloadMasterExcel,
  onGenerateCertificate,
  onGenerateIncrementLetter,
  onGenerateAppointmentLetter,
  onOpenCertificateModal
}) {
  // Navigation tabs
  const [activeTab, setActiveTab] = useState("employee_master"); // employee_master | salary_hike | attendance | leave_balance | leave_audit | payroll

  // Filters
  const [deptFilter, setDeptFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("active"); // all | active | relieved
  const [searchQuery, setSearchQuery] = useState("");
  const [yearFilter, setYearFilter] = useState(() => new Date().getFullYear());
  const [monthFilter, setMonthFilter] = useState(() => new Date().getMonth() + 1);

  // Column customization states
  const [showColPicker, setShowColPicker] = useState(false);
  const [empColSelection, setEmpColSelection] = useState(() => {
    const s = {};
    EMPLOYEE_COLUMNS.forEach(c => { s[c.key] = c.mandatory || c.default; });
    return s;
  });

  const [hikeColSelection, setHikeColSelection] = useState(() => {
    const s = {};
    SALARY_HIKE_COLUMNS.forEach(c => { s[c.key] = c.mandatory || c.default; });
    return s;
  });

  // Attendance sub-view mode & date range
  const [attMode, setAttMode] = useState("summary"); // summary | daily
  const [rangeFrom, setRangeFrom] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
  });
  const [rangeTo, setRangeTo] = useState(() => new Date().toISOString().split("T")[0]);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [loadingAttendance, setLoadingAttendance] = useState(false);

  // Leave data
  const [leaveRecords, setLeaveRecords] = useState([]);
  const [loadingLeaves, setLoadingLeaves] = useState(false);

  // Salary records for payroll register
  const [payrollMonthData, setPayrollMonthData] = useState([]);
  const [loadingPayroll, setLoadingPayroll] = useState(false);

  // Sort states for all tabs (default sorted by employee id / code ascending)
  const [empSortCol, setEmpSortCol] = useState("employee_code");
  const [empSortDir, setEmpSortDir] = useState("asc");

  const [hikeSortCol, setHikeSortCol] = useState("employee_code");
  const [hikeSortDir, setHikeSortDir] = useState("asc");

  const [attSummarySortCol, setAttSummarySortCol] = useState("employee_code");
  const [attSummarySortDir, setAttSummarySortDir] = useState("asc");

  const [attLogsSortCol, setAttLogsSortCol] = useState("employee_code");
  const [attLogsSortDir, setAttLogsSortDir] = useState("asc");

  const [leaveSortCol, setLeaveSortCol] = useState("employee_code");
  const [leaveSortDir, setLeaveSortDir] = useState("asc");

  const [payrollSortCol, setPayrollSortCol] = useState("employee_code");
  const [payrollSortDir, setPayrollSortDir] = useState("asc");

  // Company Leave Config
  const cfg = company?.config || {};
  const leaveTypes = useMemo(() => {
    if (cfg.leave_types && cfg.leave_types.length > 0) return cfg.leave_types;
    return [
      { name: "Casual Leave", days: 12 },
      { name: "Sick Leave", days: 6 },
    ];
  }, [cfg.leave_types]);

  // Departments list
  const departments = useMemo(() => {
    const s = new Set();
    employees.forEach(e => { if (e.department) s.add(e.department); });
    return Array.from(s).sort();
  }, [employees]);

  // ── Fetch Leaves on mount / tab change
  const fetchLeaves = useCallback(async () => {
    if (!supabase || !company?.id) return;
    setLoadingLeaves(true);
    try {
      const { data, error } = await supabase
        .from("employee_leaves")
        .select("*, employees(name, employee_code, department, date_of_joining)")
        .eq("company_id", company.id);
      if (!error && data) {
        setLeaveRecords(data);
      }
    } catch (e) {
      console.error("Error fetching leaves:", e);
    } finally {
      setLoadingLeaves(false);
    }
  }, [supabase, company?.id]);

  // ── Fetch Attendance on range change
  const fetchAttendance = useCallback(async () => {
    if (!supabase || !company?.id || !rangeFrom || !rangeTo) return;
    setLoadingAttendance(true);
    try {
      const { data, error } = await supabase
        .from("attendance_records")
        .select("*, employees(name, employee_code, department, designation)")
        .eq("company_id", company.id)
        .gte("date", rangeFrom)
        .lte("date", rangeTo)
        .order("date", { ascending: true });
      if (!error && data) {
        setAttendanceRecords(data);
      }
    } catch (e) {
      console.error("Error fetching attendance records:", e);
    } finally {
      setLoadingAttendance(false);
    }
  }, [supabase, company?.id, rangeFrom, rangeTo]);

  // ── Fetch Payroll Records for selected Month/Year
  const fetchPayrollMonth = useCallback(async () => {
    if (!supabase || !company?.id) return;
    setLoadingPayroll(true);
    try {
      const { data, error } = await supabase
        .from("salary_records")
        .select("*, employees(name, employee_code, department, designation, bank_name, account_number, ifsc_code)")
        .eq("company_id", company.id)
        .eq("month", monthFilter)
        .eq("year", yearFilter);
      if (!error && data) {
        setPayrollMonthData(data);
      }
    } catch (e) {
      console.error("Error fetching payroll data:", e);
    } finally {
      setLoadingPayroll(false);
    }
  }, [supabase, company?.id, monthFilter, yearFilter]);

  useEffect(() => {
    if (activeTab === "leave_balance" || activeTab === "leave_audit") {
      fetchLeaves();
    } else if (activeTab === "attendance") {
      fetchAttendance();
    } else if (activeTab === "payroll") {
      fetchPayrollMonth();
    }
  }, [activeTab, fetchLeaves, fetchAttendance, fetchPayrollMonth]);

  // ───────────────────────────────────────────────────────────────────────────
  // ── 1. FILTERED EMPLOYEES DATA (EMPLOYEE MASTER REPORT)
  // ───────────────────────────────────────────────────────────────────────────
  const filteredEmployees = useMemo(() => {
    const list = employees.filter(emp => {
      if (deptFilter !== "all" && emp.department !== deptFilter) return false;
      if (statusFilter === "active" && emp.status === "relieved") return false;
      if (statusFilter === "relieved" && emp.status !== "relieved") return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const code = (emp.employee_code || "").toLowerCase();
        const name = (emp.name || "").toLowerCase();
        const dept = (emp.department || "").toLowerCase();
        const desig = (emp.designation || "").toLowerCase();
        const email = (emp.email || "").toLowerCase();
        const phone = (emp.phone || "").toLowerCase();
        if (!code.includes(q) && !name.includes(q) && !dept.includes(q) && !desig.includes(q) && !email.includes(q) && !phone.includes(q)) {
          return false;
        }
      }
      return true;
    });

    list.sort((a, b) => {
      let res = 0;
      if (empSortCol === "name") {
        res = cmpEmpName(a, b);
      } else if (empSortCol === "employee_code") {
        res = cmpEmpId(a, b);
      } else {
        const valA = (a[empSortCol] || "").toString().trim();
        const valB = (b[empSortCol] || "").toString().trim();
        res = valA.localeCompare(valB, undefined, { numeric: true, sensitivity: "base" });
        if (res === 0) res = cmpEmpId(a, b);
      }
      return empSortDir === "asc" ? res : -res;
    });

    return list;
  }, [employees, deptFilter, statusFilter, searchQuery, empSortCol, empSortDir]);

  // ───────────────────────────────────────────────────────────────────────────
  // ── 2. SALARY HIKE RECORDS DATA
  // ───────────────────────────────────────────────────────────────────────────
  const allHikesList = useMemo(() => {
    const hikesMap = cfg.salary_hikes || {};
    const empMap = {};
    employees.forEach(e => { empMap[e.id] = e; });

    const list = [];
    Object.entries(hikesMap).forEach(([empId, hikes]) => {
      const emp = empMap[empId];
      if (!emp) return;
      if (deptFilter !== "all" && emp.department !== deptFilter) return;

      sortSalaryRevisions(Array.isArray(hikes) ? hikes : []).forEach((hike, idx) => {
        const effDate = hike.effective_date || hike.date || "—";
        const hikeYear = (effDate && effDate !== "—") ? new Date(effDate).getFullYear() : null;
        if (yearFilter !== "all" && hikeYear && hikeYear !== Number(yearFilter)) return;

        const reason = hike.reason || hike.note || "Annual Appraisal";
        if (searchQuery) {
          const q = searchQuery.toLowerCase();
          const code = (emp.employee_code || "").toLowerCase();
          const name = (emp.name || "").toLowerCase();
          const rText = reason.toLowerCase();
          if (!code.includes(q) && !name.includes(q) && !rText.includes(q)) return;
        }

        const oldBase = parseFloat(hike.old_base !== undefined ? hike.old_base : (hike.old_salary !== undefined ? hike.old_salary : 0)) || 0;
        const newBase = parseFloat(hike.new_base !== undefined ? hike.new_base : (hike.new_salary !== undefined ? hike.new_salary : 0)) || 0;
        const hikeAmt = newBase - oldBase;
        const hikePct = hike.hike_percent !== undefined
          ? parseFloat(hike.hike_percent)
          : (hike.hike_pct !== undefined ? parseFloat(hike.hike_pct) : (oldBase > 0 ? ((hikeAmt / oldBase) * 100) : 0));

        list.push({
          id: `${empId}-${idx}`,
          employee_id: empId,
          employee: emp,
          employee_code: emp.employee_code || "—",
          name: emp.name,
          department: emp.department || "—",
          old_base: oldBase,
          new_base: newBase,
          hike_amount: hikeAmt,
          hike_percent: Math.round(hikePct * 10) / 10,
          effective_date: effDate,
          reason: reason,
          approved_by: hike.approved_by || "Admin",
          created_at: hike.created_at ? new Date(hike.created_at).toLocaleDateString("en-IN") : (effDate !== "—" ? new Date(effDate).toLocaleDateString("en-IN") : "—"),
          rawHike: {
            ...hike,
            old_salary: oldBase,
            new_salary: newBase,
            hike_pct: Math.round(hikePct * 10) / 10,
            date: effDate,
            note: reason
          }
        });
      });
    });

    list.sort((a, b) => {
      let res = 0;
      if (hikeSortCol === "name") {
        res = cmpEmpName(a.employee || a, b.employee || b);
      } else if (hikeSortCol === "employee_code") {
        res = cmpEmpId(a.employee || a, b.employee || b);
      } else if (hikeSortCol === "effective_date") {
        res = new Date(a.effective_date === "—" ? 0 : a.effective_date) - new Date(b.effective_date === "—" ? 0 : b.effective_date);
        if (res === 0) res = cmpEmpId(a.employee || a, b.employee || b);
      } else {
        res = cmpEmpId(a.employee || a, b.employee || b);
      }
      return hikeSortDir === "asc" ? res : -res;
    });

    return list;
  }, [cfg.salary_hikes, employees, deptFilter, yearFilter, searchQuery, hikeSortCol, hikeSortDir]);

  // ───────────────────────────────────────────────────────────────────────────
  // ── 3. ATTENDANCE AGGREGATION & SORTING
  // ───────────────────────────────────────────────────────────────────────────
  const attendanceSummary = useMemo(() => {
    const stdIn = cfg.attendance_punch_in || "09:00";
    const stdOut = cfg.attendance_punch_out || "18:00";
    const graceMin = cfg.attendance_grace_minutes !== undefined ? cfg.attendance_grace_minutes : 15;

    const [stdInH, stdInM] = stdIn.split(":").map(Number);
    const expectedInMins = (stdInH * 60) + (stdInM || 0) + graceMin;
    const [stdOutH, stdOutM] = stdOut.split(":").map(Number);
    const expectedOutMins = (stdOutH * 60) + (stdOutM || 0);

    const statsMap = new Map();
    employees.forEach(emp => {
      if (deptFilter !== "all" && emp.department !== deptFilter) return;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        if (!(emp.employee_code || "").toLowerCase().includes(q) && !(emp.name || "").toLowerCase().includes(q)) return;
      }
      statsMap.set(emp.id, {
        id: emp.id,
        code: emp.employee_code || "—",
        employee_code: emp.employee_code || "—",
        name: emp.name,
        department: emp.department || "—",
        presentDays: 0,
        lateDays: 0,
        earlyDays: 0,
        regularizedDays: 0,
        totalMinutes: 0,
      });
    });

    attendanceRecords.forEach(rec => {
      const s = statsMap.get(rec.employee_id);
      if (!s) return;

      if (rec.regularization_status === "approved" || rec.status === "regularized") {
        s.presentDays++;
        s.regularizedDays++;
      } else if (rec.punch_in) {
        s.presentDays++;
        const inD = new Date(rec.punch_in);
        const inMins = inD.getHours() * 60 + inD.getMinutes();
        if (inMins > expectedInMins) s.lateDays++;

        if (rec.punch_out) {
          const outD = new Date(rec.punch_out);
          const outMins = outD.getHours() * 60 + outD.getMinutes();
          if (outMins < expectedOutMins) s.earlyDays++;

          const diffMs = outD - inD;
          if (diffMs > 0) s.totalMinutes += Math.floor(diffMs / 60000);
        }
      }
    });

    const list = Array.from(statsMap.values());
    list.sort((a, b) => {
      let res = 0;
      if (attSummarySortCol === "name") {
        res = cmpEmpName(a, b);
      } else {
        res = cmpEmpId(a, b);
      }
      return attSummarySortDir === "asc" ? res : -res;
    });

    return list;
  }, [attendanceRecords, employees, cfg, deptFilter, searchQuery, attSummarySortCol, attSummarySortDir]);

  // Detailed punch logs sorted
  const sortedAttendanceRecords = useMemo(() => {
    const list = [...attendanceRecords];
    list.sort((a, b) => {
      let res = 0;
      if (attLogsSortCol === "name") {
        res = cmpEmpName(a.employees || a, b.employees || b);
      } else if (attLogsSortCol === "employee_code") {
        res = cmpEmpId(a.employees || a, b.employees || b);
      } else {
        res = cmpEmpId(a.employees || a, b.employees || b);
      }
      return attLogsSortDir === "asc" ? res : -res;
    });
    return list;
  }, [attendanceRecords, attLogsSortCol, attLogsSortDir]);

  // ───────────────────────────────────────────────────────────────────────────
  // ── 4. LEAVE BALANCES & AUDIT REGISTER
  // ───────────────────────────────────────────────────────────────────────────
  const leaveAuditData = useMemo(() => {
    const list = employees.filter(emp => {
      if (deptFilter !== "all" && emp.department !== deptFilter) return false;
      if (statusFilter === "active" && emp.status === "relieved") return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        if (!(emp.employee_code || "").toLowerCase().includes(q) && !(emp.name || "").toLowerCase().includes(q)) return false;
      }
      return true;
    }).map(emp => {
      let metrics = [];
      if (typeof getEmployeeLeaveMetrics === "function") {
        metrics = getEmployeeLeaveMetrics(emp, cfg, leaveRecords, null, Number(yearFilter));
      } else {
        // Fallback calculation
        metrics = leaveTypes.map(lt => {
          const approved = leaveRecords
            .filter(l => l.employee_id === emp.id && l.status === "approved" && (l.leave_type || "").toLowerCase() === (lt.name || "").toLowerCase())
            .reduce((sum, l) => sum + (parseFloat(l.days) || 0), 0);
          const quota = parseFloat(lt.days) || 0;
          return {
            name: lt.name,
            totalQuota: quota,
            approvedUsed: approved,
            remainingBalance: Math.max(0, quota - approved),
          };
        });
      }

      const totalQuota = metrics.reduce((acc, m) => acc + (m.totalQuota || 0), 0);
      const totalAvailed = metrics.reduce((acc, m) => acc + (m.approvedUsed || 0), 0);
      const totalBalance = metrics.reduce((acc, m) => acc + (m.remainingBalance || 0), 0);

      // Loss of pay days
      const lopDays = leaveRecords
        .filter(l => l.employee_id === emp.id && l.status === "approved" && ((l.leave_type || "").toLowerCase().includes("lop") || (l.leave_type || "").toLowerCase().includes("loss of pay")))
        .reduce((sum, l) => sum + (parseFloat(l.days) || 0), 0);

      return {
        emp,
        metrics,
        totalQuota,
        totalAvailed,
        totalBalance,
        lopDays,
      };
    });

    list.sort((a, b) => {
      let res = 0;
      if (leaveSortCol === "name") {
        res = cmpEmpName(a.emp, b.emp);
      } else {
        res = cmpEmpId(a.emp, b.emp);
      }
      return leaveSortDir === "asc" ? res : -res;
    });

    return list;
  }, [employees, cfg, leaveRecords, yearFilter, deptFilter, statusFilter, searchQuery, getEmployeeLeaveMetrics, leaveTypes, leaveSortCol, leaveSortDir]);

  // ───────────────────────────────────────────────────────────────────────────
  // ── 5. PAYROLL REGISTER SORTED DATA
  // ───────────────────────────────────────────────────────────────────────────
  const sortedPayrollData = useMemo(() => {
    const list = [...payrollMonthData];
    list.sort((a, b) => {
      let res = 0;
      if (payrollSortCol === "name") {
        res = cmpEmpName(a.employees || a, b.employees || b);
      } else {
        res = cmpEmpId(a.employees || a, b.employees || b);
      }
      return payrollSortDir === "asc" ? res : -res;
    });
    return list;
  }, [payrollMonthData, payrollSortCol, payrollSortDir]);

  // ───────────────────────────────────────────────────────────────────────────
  // ── EXPORT HANDLERS
  // ───────────────────────────────────────────────────────────────────────────
  const handleExportEmployeeMaster = () => {
    const activeCols = EMPLOYEE_COLUMNS.filter(c => empColSelection[c.key]);
    const rows = filteredEmployees.map(emp => {
      const obj = {};
      activeCols.forEach(col => {
        let val = emp[col.key];
        if (col.key === "base_salary" || col.key === "monthly_gross" || col.key === "ctc") {
          val = val ? `₹${Math.round(Number(val) || 0).toLocaleString("en-IN")}` : "—";
        } else if (col.key === "date_of_joining" || col.key === "date_of_birth") {
          val = val ? new Date(val).toLocaleDateString("en-IN") : "—";
        } else if (!val) {
          val = "—";
        }
        obj[col.label] = val;
      });
      return obj;
    });

    exportStyledWorkbook(
      [{
        name: "Employee Details",
        title: `${company.name} — Employee Master Directory`,
        subtitle: `Generated on ${new Date().toLocaleDateString("en-IN")} | ${rows.length} Total Records`,
        data: rows,
      }],
      `${company.name}_Employee_Master_${new Date().toISOString().split("T")[0]}`
    );
  };

  const handleExportSalaryHikes = () => {
    const activeCols = SALARY_HIKE_COLUMNS.filter(c => hikeColSelection[c.key]);
    const rows = allHikesList.map(h => {
      const obj = {};
      activeCols.forEach(col => {
        let val = h[col.key];
        if (col.key === "old_base" || col.key === "new_base" || col.key === "hike_amount") {
          val = val ? `₹${Math.round(Number(val) || 0).toLocaleString("en-IN")}` : "₹0";
        } else if (col.key === "hike_percent") {
          val = `${val}%`;
        }
        obj[col.label] = val;
      });
      return obj;
    });

    exportStyledWorkbook(
      [{
        name: "Salary Revisions",
        title: `${company.name} — Salary Revision & Appraisal Register`,
        subtitle: `Year: ${yearFilter} | Total Appraisals: ${rows.length}`,
        data: rows,
      }],
      `${company.name}_Salary_Hikes_Report_${yearFilter}`
    );
  };

  const handleExportAttendance = () => {
    if (attMode === "summary") {
      const rows = attendanceSummary.map(s => ({
        "Employee Code": s.code,
        "Employee Name": s.name,
        "Department": s.department,
        "Present Days": s.presentDays,
        "Late Arrivals": s.lateDays,
        "Early Departures": s.earlyDays,
        "Regularized Days": s.regularizedDays,
        "Total Hours Worked": `${Math.floor(s.totalMinutes / 60)}h ${s.totalMinutes % 60}m`,
      }));

      exportStyledWorkbook(
        [{
          name: "Attendance Summary",
          title: `${company.name} — Attendance Summary Register`,
          subtitle: `Period: ${rangeFrom} to ${rangeTo}`,
          data: rows,
        }],
        `${company.name}_Attendance_Summary_${rangeFrom}_to_${rangeTo}`
      );
    } else {
      const rows = sortedAttendanceRecords.map(r => ({
        "Date": r.date,
        "Employee Code": r.employees?.employee_code || "—",
        "Employee Name": r.employees?.name || "—",
        "Department": r.employees?.department || "—",
        "Punch In": r.punch_in ? new Date(r.punch_in).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true }) : "—",
        "Punch Out": r.punch_out ? new Date(r.punch_out).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true }) : "—",
        "Status": r.status || (r.punch_in ? "Present" : "Absent"),
        "Regularization": r.regularization_status || "—",
      }));

      exportStyledWorkbook(
        [{
          name: "Daily Punch Log",
          title: `${company.name} — Daily Punch Logs`,
          subtitle: `Period: ${rangeFrom} to ${rangeTo} | ${rows.length} Total Punches`,
          data: rows,
        }],
        `${company.name}_Daily_Attendance_Logs_${rangeFrom}_to_${rangeTo}`
      );
    }
  };

  const handleExportLeaveBalances = () => {
    const rows = leaveAuditData.map(item => {
      const row = {
        "Employee Code": item.emp.employee_code || "—",
        "Employee Name": item.emp.name,
        "Department": item.emp.department || "—",
        "Date of Joining": item.emp.date_of_joining ? new Date(item.emp.date_of_joining).toLocaleDateString("en-IN") : "—",
      };

      item.metrics.forEach(m => {
        row[`${m.name} (Quota)`] = m.totalQuota;
        row[`${m.name} (Used)`] = m.approvedUsed;
        row[`${m.name} (Balance)`] = m.remainingBalance;
      });

      row["Total Quota"] = item.totalQuota;
      row["Total Availed"] = item.totalAvailed;
      row["Net Available Balance"] = item.totalBalance;
      row["Loss of Pay (Days)"] = item.lopDays;
      return row;
    });

    exportStyledWorkbook(
      [{
        name: "Leave Balances",
        title: `${company.name} — Employee Leave Balance & Quota Register`,
        subtitle: `Assessment Year: ${yearFilter} | Company Policy Quotas Included`,
        data: rows,
      }],
      `${company.name}_Leave_Balances_Report_${yearFilter}`
    );
  };

  const handleExportLeaveAudit = () => {
    // 1. Leave Rules / Quotas Sheet
    const rulesRows = leaveTypes.map(lt => ({
      "Leave Type": lt.name,
      "Annual Entitlement (Days)": lt.days,
      "Statutory / Company Policy": "Statutory Standard Paid Leave",
      "Pro-Rata for New Joinees": "Yes (based on joining month)",
    }));

    // 2. Full Employee Register Sheet
    const registerRows = leaveAuditData.map((item, idx) => {
      const row = {
        "Sl No": idx + 1,
        "Employee ID": item.emp.employee_code || "—",
        "Employee Name": item.emp.name,
        "Department": item.emp.department || "—",
        "Date of Joining": item.emp.date_of_joining ? new Date(item.emp.date_of_joining).toLocaleDateString("en-IN") : "—",
      };

      item.metrics.forEach(m => {
        row[`${m.name} Entitled`] = m.totalQuota;
        row[`${m.name} Availed`] = m.approvedUsed;
        row[`${m.name} Closing`] = m.remainingBalance;
      });

      row["Total Paid Leaves Taken"] = item.totalAvailed;
      row["Loss of Pay (LOP)"] = item.lopDays;
      row["Net Balance"] = item.totalBalance;
      return row;
    });

    exportStyledWorkbook(
      [
        {
          name: "Leave Audit Register",
          title: `${company.name} — Statutory Leave & Attendance Audit Register`,
          subtitle: `Assessment Year: ${yearFilter} | Certified Statutory Labor Compliance Register`,
          data: registerRows,
        },
        {
          name: "Company Leave Policy Rules",
          title: `${company.name} — Approved Company Leave Entitlements`,
          subtitle: `Year: ${yearFilter}`,
          data: rulesRows,
        },
      ],
      `${company.name}_Statutory_Leave_Audit_Register_${yearFilter}`
    );
  };

  const handleExportPayrollRegister = () => {
    const rows = sortedPayrollData.map(sal => ({
      "Employee Code": sal.employees?.employee_code || "—",
      "Employee Name": sal.employees?.name || "—",
      "Department": sal.employees?.department || "—",
      "Designation": sal.employees?.designation || "—",
      "Bank Name": sal.employees?.bank_name || "—",
      "Account No": sal.employees?.account_number || "—",
      "Basic Pay (₹)": Math.round(Number(sal.basic) || 0),
      "HRA (₹)": Math.round(Number(sal.hra) || 0),
      "Special Allowance (₹)": Math.round(Number(sal.special_allowance) || 0),
      "Gross Salary (₹)": Math.round(Number(sal.gross) || 0),
      "PF Employee (₹)": Math.round(Number(sal.pf_employee) || 0),
      "ESI Employee (₹)": Math.round(Number(sal.esi_employee) || 0),
      "Prof Tax PT (₹)": Math.round(Number(sal.professional_tax) || 0),
      "TDS (₹)": Math.round(Number(sal.tds) || 0),
      "Total Deductions (₹)": Math.round(
        (Number(sal.pf_employee) || 0) +
        (Number(sal.esi_employee) || 0) +
        (Number(sal.professional_tax) || 0) +
        (Number(sal.tds) || 0) +
        (Number(sal.absence_deduction) || 0)
      ),
      "Net Payout (₹)": Math.round(Number(sal.net_pay) || 0),
      "Status": (sal.status || "draft").toUpperCase(),
    }));

    exportStyledWorkbook(
      [{
        name: "Salary Register",
        title: `${company.name} — Monthly Payroll & Salary Register`,
        subtitle: `Month: ${monthFilter}/${yearFilter} | ${rows.length} Processed Employees`,
        data: rows,
      }],
      `${company.name}_Salary_Register_${monthFilter}_${yearFilter}`
    );
  };

  // ───────────────────────────────────────────────────────────────────────────
  // ── RENDER COMPONENT
  // ───────────────────────────────────────────────────────────────────────────
  return (
    <div className="reports-hub-wrapper" style={{ paddingBottom: 60 }}>
      {/* ── CSS for Smooth Hover Effects, Badges, and Clean Print Media ── */}
      <style>{`
        .rep-card {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          transition: all 0.22s cubic-bezier(0.16, 1, 0.3, 1);
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
        }
        .rep-card:hover {
          border-color: #93c5fd;
          box-shadow: 0 8px 24px -4px rgba(37, 99, 235, 0.08), 0 2px 6px -1px rgba(0, 0, 0, 0.04);
          transform: translateY(-2px);
        }
        .rep-tab-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 18px;
          border-radius: 10px;
          font-size: 13.5px;
          font-weight: 700;
          cursor: pointer;
          border: 1px solid transparent;
          background: transparent;
          color: #475569;
          transition: all 0.2s ease;
          white-space: nowrap;
        }
        .rep-tab-btn:hover {
          color: #1d4ed8;
          background: #eff6ff;
          border-color: #bfdbfe;
          transform: translateY(-1px);
        }
        .rep-tab-btn.active {
          background: #2563eb;
          color: #ffffff;
          box-shadow: 0 4px 12px rgba(37, 99, 235, 0.25);
          border-color: #1d4ed8;
        }
        .rep-btn-primary {
          background: linear-gradient(180deg, #ffffff 0%, #f1f5f9 50%, #e2e8f0 100%);
          color: #0f172a;
          border: 1.5px solid #cbd5e1;
          padding: 8px 16px;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 700;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.9);
          transition: all 0.2s ease;
        }
        .rep-btn-primary:hover {
          background: linear-gradient(180deg, #ffffff 0%, #e2e8f0 50%, #cbd5e1 100%);
          color: #020617;
          border-color: #94a3b8;
          transform: translateY(-1.5px);
          box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08), inset 0 1px 0 rgba(255, 255, 255, 1);
        }
        .rep-btn-primary:active {
          transform: translateY(0);
          box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
        }
        .rep-btn-secondary {
          background: #ffffff;
          color: #334155;
          border: 1px solid #cbd5e1;
          padding: 8px 14px;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 700;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          transition: all 0.2s ease;
        }
        .rep-btn-secondary:hover {
          background: #f8fafc;
          border-color: #94a3b8;
          color: #0f172a;
          transform: translateY(-1px);
        }
        .rep-btn-print {
          background: #475569;
          color: #ffffff;
          border: 1px solid #334155;
          padding: 8px 16px;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 700;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          transition: all 0.2s ease;
        }
        .rep-btn-print:hover {
          background: #334155;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(71, 85, 105, 0.25);
        }
        .rep-table-row {
          transition: background 0.15s ease;
        }
        .rep-table-row:hover {
          background: #f8fafc !important;
        }
        .rep-kpi-card {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          padding: 14px 18px;
          transition: all 0.2s ease;
        }
        .rep-kpi-card:hover {
          border-color: #93c5fd;
          transform: translateY(-2px);
          box-shadow: 0 6px 16px -2px rgba(37, 99, 235, 0.08);
        }

        /* ── PRINT STYLESHEET ── */
        @media print {
          body {
            background: #ffffff !important;
            color: #000000 !important;
            font-size: 11pt !important;
          }
          .no-print, .company-sidebar, .company-sidebar-header, .company-nav, .rep-tabs-bar, .rep-toolbar, .btn, button {
            display: none !important;
          }
          .print-only {
            display: block !important;
          }
          .reports-hub-wrapper {
            padding: 0 !important;
            margin: 0 !important;
            width: 100% !important;
          }
          .rep-card {
            border: 1px solid #000000 !important;
            box-shadow: none !important;
            transform: none !important;
            border-radius: 0 !important;
          }
          table {
            border-collapse: collapse !important;
            width: 100% !important;
          }
          th, td {
            border: 1px solid #000000 !important;
            color: #000000 !important;
            padding: 6px 8px !important;
            font-size: 9.5pt !important;
          }
          th {
            background: #f1f5f9 !important;
          }
        }
      `}</style>

      {/* ── Page Header ── */}
      <div className="no-print" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <span style={{ fontSize: 24 }}>📑</span>
            <span style={{ fontSize: 22, fontWeight: 800, color: "#0f172a", letterSpacing: -0.4 }}>Reports & Compliance Hub</span>
            <span style={{ background: "#eff6ff", color: "#1d4ed8", fontSize: 11, fontWeight: 700, padding: "3px 8px", borderRadius: 12, border: "1px solid #bfdbfe" }}>
              {activeCompanyBadge(company)}
            </span>
          </div>
          <div style={{ color: "#64748b", fontSize: 13 }}>
            Generate executive summaries, audit-ready registers, customized data exports, and print reports.
          </div>
        </div>

        {onBack && (
          <button className="rep-btn-secondary" onClick={onBack}>
            ← Back to Dashboard
          </button>
        )}
      </div>

      {/* ── Top Report Navigation Tabs ── */}
      <div className="no-print rep-tabs-bar" style={{ display: "flex", gap: 6, background: "#f8fafc", padding: 6, borderRadius: 12, border: "1px solid #e2e8f0", marginBottom: 20, overflowX: "auto" }}>
        {[
          { id: "employee_master", label: "👥 Employee Master", sub: "Profiles & KYC" },
          { id: "salary_hike", label: "📈 Salary Revisions", sub: "Appraisal History" },
          { id: "attendance", label: "⏱️ Attendance Register", sub: "Summary & Logs" },
          { id: "leave_balance", label: "🌴 Leave Balances", sub: "Quotas & Available" },
          { id: "leave_audit", label: "📜 Statutory Leave Audit", sub: "Ready to Print" },
          { id: "payroll", label: "💰 Payroll Register", sub: "Monthly Payouts" },
        ].map(tab => (
          <button
            key={tab.id}
            className={`rep-tab-btn ${activeTab === tab.id ? "active" : ""}`}
            onClick={() => { setActiveTab(tab.id); setShowColPicker(false); }}
          >
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* ── Common Filter & Action Toolbar ── */}
      <div className="no-print rep-card rep-toolbar" style={{ padding: "14px 18px", marginBottom: 20, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          {/* Text Search */}
          <div style={{ position: "relative" }}>
            <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", fontSize: 13 }}>🔍</span>
            <input
              type="text"
              placeholder="Search by name, ID, dept..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              style={{ padding: "8px 12px 8px 30px", borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 13, outline: "none", width: 220 }}
            />
          </div>

          {/* Department Filter */}
          <select
            value={deptFilter}
            onChange={e => setDeptFilter(e.target.value)}
            style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 13, outline: "none", background: "#fff", cursor: "pointer" }}
          >
            <option value="all">All Departments</option>
            {departments.map(d => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>

          {/* Status Filter for Employee / Leave */}
          {(activeTab === "employee_master" || activeTab === "leave_balance" || activeTab === "leave_audit") && (
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 13, outline: "none", background: "#fff", cursor: "pointer" }}
            >
              <option value="active">Active Employees Only</option>
              <option value="all">All Employees (Incl. Relieved)</option>
              <option value="relieved">Relieved Employees Only</option>
            </select>
          )}

          {/* Year Filter for Hikes, Leaves, and Payroll */}
          {(activeTab === "salary_hike" || activeTab === "leave_balance" || activeTab === "leave_audit" || activeTab === "payroll") && (
            <select
              value={yearFilter}
              onChange={e => setYearFilter(e.target.value)}
              style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 13, outline: "none", background: "#fff", cursor: "pointer" }}
            >
              {[2024, 2025, 2026, 2027].map(y => (
                <option key={y} value={y}>Year {y}</option>
              ))}
            </select>
          )}

          {/* Month Filter for Payroll */}
          {activeTab === "payroll" && (
            <select
              value={monthFilter}
              onChange={e => setMonthFilter(Number(e.target.value))}
              style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 13, outline: "none", background: "#fff", cursor: "pointer" }}
            >
              {["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"].map((m, idx) => (
                <option key={m} value={idx + 1}>{m}</option>
              ))}
            </select>
          )}

          {/* Attendance Date Range & Mode */}
          {activeTab === "attendance" && (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ display: "flex", background: "#f1f5f9", padding: 2, borderRadius: 8, border: "1px solid #cbd5e1" }}>
                <button
                  onClick={() => setAttMode("summary")}
                  style={{
                    padding: "6px 12px", borderRadius: 6, fontSize: 12, fontWeight: 700, border: "none", cursor: "pointer",
                    background: attMode === "summary" ? "#ffffff" : "transparent",
                    color: attMode === "summary" ? "#2563eb" : "#64748b",
                    boxShadow: attMode === "summary" ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
                  }}
                >
                  Summary Matrix
                </button>
                <button
                  onClick={() => setAttMode("daily")}
                  style={{
                    padding: "6px 12px", borderRadius: 6, fontSize: 12, fontWeight: 700, border: "none", cursor: "pointer",
                    background: attMode === "daily" ? "#ffffff" : "transparent",
                    color: attMode === "daily" ? "#2563eb" : "#64748b",
                    boxShadow: attMode === "daily" ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
                  }}
                >
                  Daily Punches
                </button>
              </div>
              <input
                type="date"
                value={rangeFrom}
                onChange={e => setRangeFrom(e.target.value)}
                style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 12 }}
              />
              <span style={{ color: "#94a3b8", fontSize: 12 }}>to</span>
              <input
                type="date"
                value={rangeTo}
                onChange={e => setRangeTo(e.target.value)}
                style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 12 }}
              />
            </div>
          )}
        </div>

        {/* Action Buttons: Column Customizer & Exports */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, position: "relative" }}>
          {/* Column Customizer button for Employee Master & Salary Hike */}
          {(activeTab === "employee_master" || activeTab === "salary_hike") && (
            <div style={{ position: "relative" }}>
              <button
                className="rep-btn-secondary"
                onClick={() => setShowColPicker(!showColPicker)}
              >
                ⚙️ Customize Columns
              </button>

              {/* Column Picker Popover */}
              {showColPicker && (
                <div style={{
                  position: "absolute", right: 0, top: "115%", background: "#ffffff", border: "1px solid #cbd5e1",
                  borderRadius: 12, padding: 16, width: 320, zIndex: 100, boxShadow: "0 10px 25px -5px rgba(0,0,0,0.15)",
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, paddingBottom: 8, borderBottom: "1px solid #e2e8f0" }}>
                    <div style={{ fontWeight: 800, fontSize: 14, color: "#0f172a" }}>Display Columns</div>
                    <button
                      onClick={() => setShowColPicker(false)}
                      style={{ background: "none", border: "none", color: "#64748b", fontSize: 16, cursor: "pointer", fontWeight: 700 }}
                    >
                      ✕
                    </button>
                  </div>

                  <div style={{ maxHeight: 260, overflowY: "auto", display: "flex", flexDirection: "column", gap: 8, paddingRight: 4 }}>
                    {activeTab === "employee_master" && EMPLOYEE_COLUMNS.map(col => (
                      <label key={col.key} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, color: col.mandatory ? "#64748b" : "#1e293b", cursor: col.mandatory ? "not-allowed" : "pointer" }}>
                        <input
                          type="checkbox"
                          checked={col.mandatory || !!empColSelection[col.key]}
                          disabled={col.mandatory}
                          onChange={e => {
                            if (col.mandatory) return;
                            setEmpColSelection(prev => ({ ...prev, [col.key]: e.target.checked }));
                          }}
                        />
                        <span>{col.label}</span>
                        {col.mandatory && (
                          <span style={{ fontSize: 10, background: "#f1f5f9", color: "#64748b", padding: "1px 5px", borderRadius: 4, fontWeight: 700 }}>
                            🔒 Required
                          </span>
                        )}
                      </label>
                    ))}

                    {activeTab === "salary_hike" && SALARY_HIKE_COLUMNS.map(col => (
                      <label key={col.key} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, color: col.mandatory ? "#64748b" : "#1e293b", cursor: col.mandatory ? "not-allowed" : "pointer" }}>
                        <input
                          type="checkbox"
                          checked={col.mandatory || !!hikeColSelection[col.key]}
                          disabled={col.mandatory}
                          onChange={e => {
                            if (col.mandatory) return;
                            setHikeColSelection(prev => ({ ...prev, [col.key]: e.target.checked }));
                          }}
                        />
                        <span>{col.label}</span>
                        {col.mandatory && (
                          <span style={{ fontSize: 10, background: "#f1f5f9", color: "#64748b", padding: "1px 5px", borderRadius: 4, fontWeight: 700 }}>
                            🔒 Required
                          </span>
                        )}
                      </label>
                    ))}
                  </div>

                  <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between" }}>
                    <button
                      onClick={() => {
                        if (activeTab === "employee_master") {
                          const all = {};
                          EMPLOYEE_COLUMNS.forEach(c => all[c.key] = true);
                          setEmpColSelection(all);
                        } else {
                          const all = {};
                          SALARY_HIKE_COLUMNS.forEach(c => all[c.key] = true);
                          setHikeColSelection(all);
                        }
                      }}
                      style={{ background: "none", border: "none", color: "#2563eb", fontSize: 11.5, fontWeight: 700, cursor: "pointer" }}
                    >
                      Select All
                    </button>
                    <button
                      onClick={() => {
                        if (activeTab === "employee_master") {
                          const reset = {};
                          EMPLOYEE_COLUMNS.forEach(c => reset[c.key] = c.mandatory || c.default);
                          setEmpColSelection(reset);
                        } else {
                          const reset = {};
                          SALARY_HIKE_COLUMNS.forEach(c => reset[c.key] = c.mandatory || c.default);
                          setHikeColSelection(reset);
                        }
                      }}
                      style={{ background: "none", border: "none", color: "#64748b", fontSize: 11.5, fontWeight: 700, cursor: "pointer" }}
                    >
                      Reset Defaults
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Print Button (Special highlight for Statutory Leave Audit) */}
          {activeTab === "leave_audit" && (
            <button className="rep-btn-print" onClick={() => window.print()}>
              🖨️ Print Audit Report
            </button>
          )}

          {/* Master Excel download (same 4-sheet formula workbook as Run Payroll) */}
          {activeTab === "payroll" && onDownloadMasterExcel && (
            <button
              className="rep-btn-primary"
              onClick={() => onDownloadMasterExcel(monthFilter, yearFilter)}
              title="Download 4-sheet formula-driven Master Excel (Payroll Master, ESI, PF, Executive Summary)"
            >
              <span>📊</span> Download Master Excel
            </button>
          )}

          {/* Export Excel Button */}
          <button
            className="rep-btn-primary"
            onClick={() => {
              if (activeTab === "employee_master") handleExportEmployeeMaster();
              else if (activeTab === "salary_hike") handleExportSalaryHikes();
              else if (activeTab === "attendance") handleExportAttendance();
              else if (activeTab === "leave_balance") handleExportLeaveBalances();
              else if (activeTab === "leave_audit") handleExportLeaveAudit();
              else if (activeTab === "payroll") handleExportPayrollRegister();
            }}
          >
            📥 {activeTab === "payroll" ? "Quick Register Excel" : "Export to Excel"}
          </button>
        </div>
      </div>

      {/* ───────────────────────────────────────────────────────────────────────
          TAB 1: EMPLOYEE MASTER DETAILS REPORT
      ─────────────────────────────────────────────────────────────────────── */}
      {activeTab === "employee_master" && (
        <div className="rep-card" style={{ padding: "20px 24px", overflow: "hidden" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <div>
              <div style={{ fontWeight: 800, fontSize: 16, color: "#0f172a" }}>👥 Employee Master Details Directory</div>
              <div style={{ color: "#64748b", fontSize: 12.5, marginTop: 2 }}>
                Showing {filteredEmployees.length} employee records with {EMPLOYEE_COLUMNS.filter(c => empColSelection[c.key]).length} active columns.
              </div>
            </div>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                  {EMPLOYEE_COLUMNS.filter(c => empColSelection[c.key]).map(col => {
                    const isSortable = col.key === "employee_code" || col.key === "name";
                    const isActive = empSortCol === col.key;
                    return (
                      <th
                        key={col.key}
                        style={{
                          padding: "12px 14px",
                          color: isActive ? "#2563eb" : "#334155",
                          fontWeight: 700,
                          whiteSpace: "nowrap",
                          cursor: isSortable ? "pointer" : "default",
                          userSelect: isSortable ? "none" : "auto",
                          transition: "color 0.15s ease"
                        }}
                        onClick={isSortable ? () => {
                          if (empSortCol === col.key) setEmpSortDir(d => d === "asc" ? "desc" : "asc");
                          else { setEmpSortCol(col.key); setEmpSortDir("asc"); }
                        } : undefined}
                        title={isSortable ? `Click to sort by ${col.label}` : undefined}
                      >
                        <div style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                          <span>{col.label}</span>
                          {isSortable && (
                            <span style={{ fontSize: 11, color: isActive ? "#2563eb" : "#94a3b8" }}>
                              {isActive ? (empSortDir === "asc" ? "▲" : "▼") : "⇅"}
                            </span>
                          )}
                        </div>
                      </th>
                    );
                  })}
                  {(onGenerateAppointmentLetter || onOpenCertificateModal) && (
                    <th style={{ padding: "12px 14px", color: "#334155", fontWeight: 700, whiteSpace: "nowrap", textAlign: "right" }}>
                      Official Letters
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {filteredEmployees.length === 0 ? (
                  <tr>
                    <td colSpan={EMPLOYEE_COLUMNS.filter(c => empColSelection[c.key]).length + ((onGenerateAppointmentLetter || onOpenCertificateModal) ? 1 : 0)} style={{ textAlign: "center", padding: 32, color: "#94a3b8" }}>
                      No matching employee records found.
                    </td>
                  </tr>
                ) : (
                  filteredEmployees.map(emp => (
                    <tr key={emp.id} className="rep-table-row" style={{ borderBottom: "1px solid #f1f5f9" }}>
                      {EMPLOYEE_COLUMNS.filter(c => empColSelection[c.key]).map(col => {
                        let content = emp[col.key];
                        if (col.key === "employee_code") {
                          content = <strong style={{ color: "#1d4ed8" }}>{content || "—"}</strong>;
                        } else if (col.key === "name") {
                          content = <span style={{ fontWeight: 700, color: "#0f172a" }}>{content}</span>;
                        } else if (col.key === "status") {
                          content = (
                            <span style={{
                              display: "inline-block", padding: "2px 8px", borderRadius: 12, fontSize: 11, fontWeight: 700,
                              background: content === "relieved" ? "#f1f5f9" : "#eff6ff",
                              color: content === "relieved" ? "#64748b" : "#2563eb",
                            }}>
                              {(content || "active").toUpperCase()}
                            </span>
                          );
                        } else if (col.key === "base_salary" || col.key === "monthly_gross" || col.key === "ctc") {
                          content = content ? `₹${Math.round(Number(content) || 0).toLocaleString("en-IN")}` : "—";
                        } else if (col.key === "date_of_joining" || col.key === "date_of_birth") {
                          content = content ? new Date(content).toLocaleDateString("en-IN") : "—";
                        } else if (!content) {
                          content = "—";
                        }
                        return (
                          <td key={col.key} style={{ padding: "11px 14px", color: "#334155", whiteSpace: "nowrap" }}>
                            {content}
                          </td>
                        );
                      })}
                      {(onGenerateAppointmentLetter || onOpenCertificateModal) && (
                        <td style={{ padding: "8px 14px", whiteSpace: "nowrap", textAlign: "right" }}>
                          <div style={{ display: "inline-flex", gap: 6, justifyContent: "flex-end" }}>
                            {onGenerateAppointmentLetter && (
                              <button
                                className="rep-btn-secondary"
                                style={{ padding: "4px 8px", fontSize: 11.5, display: "inline-flex", alignItems: "center", gap: 4, cursor: "pointer" }}
                                onClick={() => {
                                  const codeClean = String(emp.employee_code || emp.id || "001").replace(/[^a-zA-Z0-9]/g, "");
                                  const defaultRef = emp.appointment_ref_no || emp.ref_no || `REF/APT/${codeClean}/${new Date().getFullYear()}`;
                                  const userRef = window.prompt("Enter Reference Number for Appointment Letter (leave blank to omit):", defaultRef);
                                  if (userRef === null) return;
                                  onGenerateAppointmentLetter(emp, company, userRef);
                                }}
                                title="Download Formal Appointment Letter PDF"
                              >
                                <span>📄</span> Appointment
                              </button>
                            )}
                            {onOpenCertificateModal && (
                              <button
                                className="rep-btn-secondary"
                                style={{ padding: "4px 8px", fontSize: 11.5, display: "inline-flex", alignItems: "center", gap: 4, cursor: "pointer" }}
                                onClick={() => onOpenCertificateModal(emp)}
                                title="Issue Salary / Employment Certificate"
                              >
                                <span>📜</span> Certificate
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ───────────────────────────────────────────────────────────────────────
          TAB 2: SALARY HIKE & REVISION REPORT
      ─────────────────────────────────────────────────────────────────────── */}
      {activeTab === "salary_hike" && (
        <div>
          {/* KPI Summary Cards */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14, marginBottom: 20 }}>
            <div className="rep-kpi-card">
              <div style={{ fontSize: 12, fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>Total Revisions</div>
              <div style={{ fontSize: 24, fontWeight: 800, color: "#1d4ed8", marginTop: 4 }}>{allHikesList.length}</div>
              <div style={{ fontSize: 11.5, color: "#94a3b8", marginTop: 2 }}>In Year {yearFilter}</div>
            </div>
            <div className="rep-kpi-card">
              <div style={{ fontSize: 12, fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>Average Hike %</div>
              <div style={{ fontSize: 24, fontWeight: 800, color: "#7e22ce", marginTop: 4 }}>
                {allHikesList.length > 0
                  ? `${(allHikesList.reduce((acc, h) => acc + h.hike_percent, 0) / allHikesList.length).toFixed(1)}%`
                  : "0.0%"}
              </div>
              <div style={{ fontSize: 11.5, color: "#94a3b8", marginTop: 2 }}>Across all appraisals</div>
            </div>
            <div className="rep-kpi-card">
              <div style={{ fontSize: 12, fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>Monthly Outflow Impact</div>
              <div style={{ fontSize: 24, fontWeight: 800, color: "#0f766e", marginTop: 4 }}>
                ₹{Math.round(allHikesList.reduce((acc, h) => acc + (h.hike_amount || 0), 0)).toLocaleString("en-IN")}
              </div>
              <div style={{ fontSize: 11.5, color: "#94a3b8", marginTop: 2 }}>Total incremental base</div>
            </div>
            <div className="rep-kpi-card">
              <div style={{ fontSize: 12, fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>Annual Cost Impact</div>
              <div style={{ fontSize: 24, fontWeight: 800, color: "#b45309", marginTop: 4 }}>
                ₹{Math.round(allHikesList.reduce((acc, h) => acc + (h.hike_amount || 0), 0) * 12).toLocaleString("en-IN")}
              </div>
              <div style={{ fontSize: 11.5, color: "#94a3b8", marginTop: 2 }}>Projected yearly revision</div>
            </div>
          </div>

          <div className="rep-card" style={{ padding: "20px 24px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: 16, color: "#0f172a" }}>📈 Salary Revision & Appraisal Register</div>
                <div style={{ color: "#64748b", fontSize: 12.5, marginTop: 2 }}>
                  Comprehensive audit trail of compensation increments, appraisal percentages, and effective dates.
                </div>
              </div>
            </div>

            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                    {SALARY_HIKE_COLUMNS.filter(c => hikeColSelection[c.key]).map(col => {
                      const isSortable = col.key === "employee_code" || col.key === "name" || col.key === "effective_date";
                      const isActive = hikeSortCol === col.key;
                      return (
                        <th
                          key={col.key}
                          style={{
                            padding: "12px 14px",
                            color: isActive ? "#2563eb" : "#334155",
                            fontWeight: 700,
                            whiteSpace: "nowrap",
                            cursor: isSortable ? "pointer" : "default",
                            userSelect: isSortable ? "none" : "auto",
                            transition: "color 0.15s ease"
                          }}
                          onClick={isSortable ? () => {
                            if (hikeSortCol === col.key) setHikeSortDir(d => d === "asc" ? "desc" : "asc");
                            else { setHikeSortCol(col.key); setHikeSortDir("asc"); }
                          } : undefined}
                          title={isSortable ? `Click to sort by ${col.label}` : undefined}
                        >
                          <div style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                            <span>{col.label}</span>
                            {isSortable && (
                              <span style={{ fontSize: 11, color: isActive ? "#2563eb" : "#94a3b8" }}>
                                {isActive ? (hikeSortDir === "asc" ? "▲" : "▼") : "⇅"}
                              </span>
                            )}
                          </div>
                        </th>
                      );
                    })}
                    {onGenerateIncrementLetter && (
                      <th style={{ padding: "12px 14px", color: "#334155", fontWeight: 700, whiteSpace: "nowrap", textAlign: "right" }}>
                        Official Letter
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {allHikesList.length === 0 ? (
                    <tr>
                      <td colSpan={SALARY_HIKE_COLUMNS.filter(c => hikeColSelection[c.key]).length + (onGenerateIncrementLetter ? 1 : 0)} style={{ textAlign: "center", padding: 32, color: "#94a3b8" }}>
                        No salary revisions found for the selected period.
                      </td>
                    </tr>
                  ) : (
                    allHikesList.map(hike => (
                      <tr key={hike.id} className="rep-table-row" style={{ borderBottom: "1px solid #f1f5f9" }}>
                        {SALARY_HIKE_COLUMNS.filter(c => hikeColSelection[c.key]).map(col => {
                          let content = hike[col.key];
                          if (col.key === "employee_code") {
                            content = <strong style={{ color: "#1d4ed8" }}>{content}</strong>;
                          } else if (col.key === "name") {
                            content = <span style={{ fontWeight: 700, color: "#0f172a" }}>{content}</span>;
                          } else if (col.key === "old_base" || col.key === "new_base" || col.key === "hike_amount") {
                            content = `₹${Math.round(Number(content || 0)).toLocaleString("en-IN")}`;
                          } else if (col.key === "hike_percent") {
                            content = (
                              <span style={{
                                background: "#eff6ff", color: "#1d4ed8", padding: "2px 8px", borderRadius: 12,
                                fontWeight: 800, fontSize: 12, display: "inline-flex", alignItems: "center", gap: 2,
                              }}>
                                ↗ +{content}%
                              </span>
                            );
                          }
                          return (
                            <td key={col.key} style={{ padding: "11px 14px", color: "#334155", whiteSpace: "nowrap" }}>
                              {content}
                            </td>
                          );
                        })}
                        {onGenerateIncrementLetter && (
                          <td style={{ padding: "8px 14px", whiteSpace: "nowrap", textAlign: "right" }}>
                            <button
                              className="rep-btn-secondary"
                              style={{ padding: "4px 10px", fontSize: 11.5, display: "inline-flex", alignItems: "center", gap: 4, cursor: "pointer" }}
                              onClick={() => onGenerateIncrementLetter({ employee: hike.employee, company, hikeRecord: hike.rawHike })}
                              title="Download Official Increment / Appraisal Letter PDF"
                            >
                              <span>📄</span> Letter
                            </button>
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ───────────────────────────────────────────────────────────────────────
          TAB 3: ATTENDANCE REGISTER REPORT
      ─────────────────────────────────────────────────────────────────────── */}
      {activeTab === "attendance" && (
        <div className="rep-card" style={{ padding: "20px 24px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <div>
              <div style={{ fontWeight: 800, fontSize: 16, color: "#0f172a" }}>
                ⏱️ Attendance {attMode === "summary" ? "Summary Register" : "Detailed Punch Logs"}
              </div>
              <div style={{ color: "#64748b", fontSize: 12.5, marginTop: 2 }}>
                Period: {rangeFrom} to {rangeTo} • {attendanceRecords.length} punch records tracked.
              </div>
            </div>
          </div>

          {loadingAttendance ? (
            <div style={{ textAlign: "center", padding: 40, color: "#64748b" }}>Loading attendance data...</div>
          ) : attMode === "summary" ? (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                    <th
                      onClick={() => {
                        if (attSummarySortCol === "employee_code") setAttSummarySortDir(d => d === "asc" ? "desc" : "asc");
                        else { setAttSummarySortCol("employee_code"); setAttSummarySortDir("asc"); }
                      }}
                      style={{
                        padding: "12px 14px",
                        color: attSummarySortCol === "employee_code" ? "#2563eb" : "#334155",
                        fontWeight: 700,
                        cursor: "pointer",
                        userSelect: "none"
                      }}
                      title="Click to sort by Employee ID"
                    >
                      <div style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                        <span>Employee ID</span>
                        <span style={{ fontSize: 11, color: attSummarySortCol === "employee_code" ? "#2563eb" : "#94a3b8" }}>
                          {attSummarySortCol === "employee_code" ? (attSummarySortDir === "asc" ? "▲" : "▼") : "⇅"}
                        </span>
                      </div>
                    </th>
                    <th
                      onClick={() => {
                        if (attSummarySortCol === "name") setAttSummarySortDir(d => d === "asc" ? "desc" : "asc");
                        else { setAttSummarySortCol("name"); setAttSummarySortDir("asc"); }
                      }}
                      style={{
                        padding: "12px 14px",
                        color: attSummarySortCol === "name" ? "#2563eb" : "#334155",
                        fontWeight: 700,
                        cursor: "pointer",
                        userSelect: "none"
                      }}
                      title="Click to sort by Employee Name"
                    >
                      <div style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                        <span>Employee Name</span>
                        <span style={{ fontSize: 11, color: attSummarySortCol === "name" ? "#2563eb" : "#94a3b8" }}>
                          {attSummarySortCol === "name" ? (attSummarySortDir === "asc" ? "▲" : "▼") : "⇅"}
                        </span>
                      </div>
                    </th>
                    <th style={{ padding: "12px 14px", color: "#334155", fontWeight: 700 }}>Department</th>
                    <th style={{ padding: "12px 14px", color: "#334155", fontWeight: 700, textAlign: "center" }}>Present Days</th>
                    <th style={{ padding: "12px 14px", color: "#334155", fontWeight: 700, textAlign: "center" }}>Late Arrivals</th>
                    <th style={{ padding: "12px 14px", color: "#334155", fontWeight: 700, textAlign: "center" }}>Early Departures</th>
                    <th style={{ padding: "12px 14px", color: "#334155", fontWeight: 700, textAlign: "center" }}>Regularized</th>
                    <th style={{ padding: "12px 14px", color: "#334155", fontWeight: 700, textAlign: "right" }}>Total Hours</th>
                  </tr>
                </thead>
                <tbody>
                  {attendanceSummary.length === 0 ? (
                    <tr>
                      <td colSpan={8} style={{ textAlign: "center", padding: 32, color: "#94a3b8" }}>No attendance records found for this period.</td>
                    </tr>
                  ) : (
                    attendanceSummary.map(s => {
                      const hrs = Math.floor(s.totalMinutes / 60);
                      const mins = s.totalMinutes % 60;
                      return (
                        <tr key={s.id} className="rep-table-row" style={{ borderBottom: "1px solid #f1f5f9" }}>
                          <td style={{ padding: "11px 14px", fontWeight: 700, color: "#1d4ed8" }}>{s.code}</td>
                          <td style={{ padding: "11px 14px", fontWeight: 700, color: "#0f172a" }}>{s.name}</td>
                          <td style={{ padding: "11px 14px", color: "#64748b" }}>{s.department}</td>
                          <td style={{ padding: "11px 14px", textAlign: "center", fontWeight: 700, color: "#16a34a" }}>{s.presentDays}</td>
                          <td style={{ padding: "11px 14px", textAlign: "center", color: s.lateDays > 0 ? "#b45309" : "#64748b", fontWeight: s.lateDays > 0 ? 700 : 400 }}>{s.lateDays}</td>
                          <td style={{ padding: "11px 14px", textAlign: "center", color: s.earlyDays > 0 ? "#b45309" : "#64748b", fontWeight: s.earlyDays > 0 ? 700 : 400 }}>{s.earlyDays}</td>
                          <td style={{ padding: "11px 14px", textAlign: "center", color: "#64748b" }}>{s.regularizedDays}</td>
                          <td style={{ padding: "11px 14px", textAlign: "right", fontWeight: 700, color: "#0f172a" }}>{hrs}h {mins}m</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                    <th style={{ padding: "12px 14px", color: "#334155", fontWeight: 700 }}>Date</th>
                    <th
                      onClick={() => {
                        if (attLogsSortCol === "employee_code") {
                          setAttLogsSortCol("name");
                          setAttLogsSortDir("asc");
                        } else {
                          setAttLogsSortCol("employee_code");
                          setAttLogsSortDir(d => d === "asc" ? "desc" : "asc");
                        }
                      }}
                      style={{
                        padding: "12px 14px",
                        color: (attLogsSortCol === "employee_code" || attLogsSortCol === "name") ? "#2563eb" : "#334155",
                        fontWeight: 700,
                        cursor: "pointer",
                        userSelect: "none"
                      }}
                      title="Click to toggle sorting by Employee ID or Name"
                    >
                      <div style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                        <span>Employee ({attLogsSortCol === "name" ? "by Name" : "by ID"})</span>
                        <span style={{ fontSize: 11, color: "#2563eb" }}>
                          {attLogsSortDir === "asc" ? "▲" : "▼"}
                        </span>
                      </div>
                    </th>
                    <th style={{ padding: "12px 14px", color: "#334155", fontWeight: 700 }}>Department</th>
                    <th style={{ padding: "12px 14px", color: "#334155", fontWeight: 700 }}>Punch In</th>
                    <th style={{ padding: "12px 14px", color: "#334155", fontWeight: 700 }}>Punch Out</th>
                    <th style={{ padding: "12px 14px", color: "#334155", fontWeight: 700 }}>Status</th>
                    <th style={{ padding: "12px 14px", color: "#334155", fontWeight: 700 }}>Regularization</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedAttendanceRecords.length === 0 ? (
                    <tr>
                      <td colSpan={7} style={{ textAlign: "center", padding: 32, color: "#94a3b8" }}>No daily punches logged in this range.</td>
                    </tr>
                  ) : (
                    sortedAttendanceRecords.map(r => (
                      <tr key={r.id} className="rep-table-row" style={{ borderBottom: "1px solid #f1f5f9" }}>
                        <td style={{ padding: "11px 14px", color: "#64748b", whiteSpace: "nowrap" }}>{r.date}</td>
                        <td style={{ padding: "11px 14px", color: "#0f172a", fontWeight: 700 }}>
                          {r.employees?.name}
                          <div style={{ fontSize: 11, color: "#64748b", fontWeight: 400 }}>{r.employees?.employee_code}</div>
                        </td>
                        <td style={{ padding: "11px 14px", color: "#64748b" }}>{r.employees?.department || "—"}</td>
                        <td style={{ padding: "11px 14px", color: "#16a34a", fontWeight: 700 }}>
                          {r.punch_in ? new Date(r.punch_in).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true }) : "—"}
                        </td>
                        <td style={{ padding: "11px 14px", color: "#2563eb", fontWeight: 700 }}>
                          {r.punch_out ? new Date(r.punch_out).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true }) : "—"}
                        </td>
                        <td style={{ padding: "11px 14px" }}>
                          <span style={{
                            display: "inline-block", padding: "2px 8px", borderRadius: 12, fontSize: 11, fontWeight: 700,
                            background: r.punch_in ? "#eff6ff" : "#fef2f2",
                            color: r.punch_in ? "#1d4ed8" : "#dc2626",
                          }}>
                            {r.status || (r.punch_in ? "Present" : "Absent")}
                          </span>
                        </td>
                        <td style={{ padding: "11px 14px", color: "#64748b", fontSize: 12 }}>
                          {r.regularization_status || "—"}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ───────────────────────────────────────────────────────────────────────
          TAB 4: LEAVE BALANCE & UTILIZATION REPORT
      ─────────────────────────────────────────────────────────────────────── */}
      {activeTab === "leave_balance" && (
        <div className="rep-card" style={{ padding: "20px 24px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <div>
              <div style={{ fontWeight: 800, fontSize: 16, color: "#0f172a" }}>🌴 Leave Balances & Availability Matrix</div>
              <div style={{ color: "#64748b", fontSize: 12.5, marginTop: 2 }}>
                Annual quota breakdown, availed leaves, and available remaining balance per employee for Year {yearFilter}.
              </div>
            </div>
          </div>

          {loadingLeaves ? (
            <div style={{ textAlign: "center", padding: 40, color: "#64748b" }}>Loading leave records...</div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                    <th
                      onClick={() => {
                        if (leaveSortCol === "employee_code") setLeaveSortDir(d => d === "asc" ? "desc" : "asc");
                        else { setLeaveSortCol("employee_code"); setLeaveSortDir("asc"); }
                      }}
                      style={{
                        padding: "12px 14px",
                        color: leaveSortCol === "employee_code" ? "#2563eb" : "#334155",
                        fontWeight: 700,
                        cursor: "pointer",
                        userSelect: "none"
                      }}
                      title="Click to sort by Employee ID"
                    >
                      <div style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                        <span>Employee ID</span>
                        <span style={{ fontSize: 11, color: leaveSortCol === "employee_code" ? "#2563eb" : "#94a3b8" }}>
                          {leaveSortCol === "employee_code" ? (leaveSortDir === "asc" ? "▲" : "▼") : "⇅"}
                        </span>
                      </div>
                    </th>
                    <th
                      onClick={() => {
                        if (leaveSortCol === "name") setLeaveSortDir(d => d === "asc" ? "desc" : "asc");
                        else { setLeaveSortCol("name"); setLeaveSortDir("asc"); }
                      }}
                      style={{
                        padding: "12px 14px",
                        color: leaveSortCol === "name" ? "#2563eb" : "#334155",
                        fontWeight: 700,
                        cursor: "pointer",
                        userSelect: "none"
                      }}
                      title="Click to sort by Employee Name"
                    >
                      <div style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                        <span>Employee Name</span>
                        <span style={{ fontSize: 11, color: leaveSortCol === "name" ? "#2563eb" : "#94a3b8" }}>
                          {leaveSortCol === "name" ? (leaveSortDir === "asc" ? "▲" : "▼") : "⇅"}
                        </span>
                      </div>
                    </th>
                    <th style={{ padding: "12px 14px", color: "#334155", fontWeight: 700 }}>Department</th>
                    {leaveTypes.map(lt => (
                      <th key={lt.name} style={{ padding: "12px 14px", color: "#334155", fontWeight: 700, textAlign: "center" }}>
                        {lt.name}
                        <div style={{ fontSize: 10, color: "#64748b", fontWeight: 400 }}>Quota / Used / Bal</div>
                      </th>
                    ))}
                    <th style={{ padding: "12px 14px", color: "#334155", fontWeight: 700, textAlign: "center" }}>Total Quota</th>
                    <th style={{ padding: "12px 14px", color: "#334155", fontWeight: 700, textAlign: "center" }}>Total Used</th>
                    <th style={{ padding: "12px 14px", color: "#334155", fontWeight: 700, textAlign: "center" }}>Available Balance</th>
                    <th style={{ padding: "12px 14px", color: "#334155", fontWeight: 700, textAlign: "center" }}>LOP Days</th>
                  </tr>
                </thead>
                <tbody>
                  {leaveAuditData.length === 0 ? (
                    <tr>
                      <td colSpan={6 + leaveTypes.length} style={{ textAlign: "center", padding: 32, color: "#94a3b8" }}>No employee leave records found.</td>
                    </tr>
                  ) : (
                    leaveAuditData.map(item => (
                      <tr key={item.emp.id} className="rep-table-row" style={{ borderBottom: "1px solid #f1f5f9" }}>
                        <td style={{ padding: "11px 14px", fontWeight: 700, color: "#1d4ed8" }}>{item.emp.employee_code || "—"}</td>
                        <td style={{ padding: "11px 14px", fontWeight: 700, color: "#0f172a" }}>{item.emp.name}</td>
                        <td style={{ padding: "11px 14px", color: "#64748b" }}>{item.emp.department || "—"}</td>

                        {item.metrics.map(m => (
                          <td key={m.name} style={{ padding: "11px 14px", textAlign: "center", whiteSpace: "nowrap" }}>
                            <span style={{ color: "#64748b" }}>{m.totalQuota}</span>
                            <span style={{ color: "#cbd5e1", margin: "0 4px" }}>/</span>
                            <span style={{ color: m.approvedUsed > 0 ? "#b45309" : "#64748b", fontWeight: m.approvedUsed > 0 ? 700 : 400 }}>{m.approvedUsed}</span>
                            <span style={{ color: "#cbd5e1", margin: "0 4px" }}>/</span>
                            <strong style={{ color: m.remainingBalance > 0 ? "#1d4ed8" : "#dc2626" }}>{m.remainingBalance}</strong>
                          </td>
                        ))}

                        <td style={{ padding: "11px 14px", textAlign: "center", color: "#475569", fontWeight: 600 }}>{item.totalQuota}</td>
                        <td style={{ padding: "11px 14px", textAlign: "center", color: "#b45309", fontWeight: 700 }}>{item.totalAvailed}</td>
                        <td style={{ padding: "11px 14px", textAlign: "center" }}>
                          <span style={{
                            display: "inline-block", padding: "2px 8px", borderRadius: 12, fontSize: 12, fontWeight: 800,
                            background: item.totalBalance > 0 ? "#eff6ff" : "#fef2f2",
                            color: item.totalBalance > 0 ? "#1d4ed8" : "#dc2626",
                          }}>
                            {item.totalBalance}
                          </span>
                        </td>
                        <td style={{ padding: "11px 14px", textAlign: "center", color: item.lopDays > 0 ? "#dc2626" : "#94a3b8", fontWeight: item.lopDays > 0 ? 800 : 400 }}>
                          {item.lopDays || 0}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ───────────────────────────────────────────────────────────────────────
          TAB 5: STATUTORY LEAVE AUDIT REGISTER (PRINT-READY)
      ─────────────────────────────────────────────────────────────────────── */}
      {activeTab === "leave_audit" && (
        <div>
          {/* Audit Summary & Policy Quotas Card */}
          <div className="rep-card" style={{ padding: "20px 24px", marginBottom: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 14 }}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 20 }}>📜</span>
                  <span style={{ fontSize: 18, fontWeight: 800, color: "#0f172a" }}>Statutory Leave Audit & Compliance Register</span>
                </div>
                <div style={{ color: "#64748b", fontSize: 13, marginTop: 4 }}>
                  Official audit-grade documentation of approved company leave numbers, annual allowances, and employee balances for labor audits.
                </div>
              </div>

              <div className="no-print" style={{ display: "flex", gap: 8 }}>
                <button className="rep-btn-print" onClick={() => window.print()}>
                  🖨️ Print Formatted Audit Sheet
                </button>
                <button className="rep-btn-primary" onClick={handleExportLeaveAudit}>
                  📥 Download Audit Excel
                </button>
              </div>
            </div>

            {/* Official Company Policy Quota Cards */}
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid #e2e8f0" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 10 }}>
                📋 Company Authorized Annual Leave Policy Quotas (Year {yearFilter}):
              </div>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                {leaveTypes.map(lt => (
                  <div key={lt.name} style={{ background: "#f8fafc", border: "1px solid #cbd5e1", borderRadius: 8, padding: "8px 14px", minWidth: 150 }}>
                    <div style={{ fontSize: 11, color: "#64748b", fontWeight: 700, textTransform: "uppercase" }}>{lt.name}</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: "#1d4ed8", marginTop: 2 }}>{lt.days} Days / Year</div>
                  </div>
                ))}
                <div style={{ background: "#f8fafc", border: "1px solid #cbd5e1", borderRadius: 8, padding: "8px 14px", minWidth: 150 }}>
                  <div style={{ fontSize: 11, color: "#64748b", fontWeight: 700, textTransform: "uppercase" }}>Work Schedule</div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: "#0f172a", marginTop: 4 }}>
                    {(cfg.working_days_schedule || [1, 2, 3, 4, 5, 6]).length} Days / Week
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Formatted Audit Document (Printable) */}
          <div className="rep-card" style={{ padding: "28px 32px" }}>
            {/* Header visible only on print & preview */}
            <div style={{ borderBottom: "2px solid #0f172a", paddingBottom: 16, marginBottom: 20 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div>
                  <div style={{ fontSize: 20, fontWeight: 900, color: "#0f172a", textTransform: "uppercase", letterSpacing: 0.5 }}>
                    {company.name}
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#475569", marginTop: 2 }}>
                    ANNUAL STATUTORY LEAVE REGISTER & ATTENDANCE AUDIT RECORD
                  </div>
                  <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                    Assessment Period: January 1, {yearFilter} – December 31, {yearFilter}
                  </div>
                </div>
                <div style={{ textAlign: "right", fontSize: 11.5, color: "#64748b" }}>
                  <div>Report Date: {new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</div>
                  <div>Audit Code: REG-LV-{yearFilter}-{company.id ? String(company.id).slice(0, 6).toUpperCase() : "CO"}</div>
                </div>
              </div>
            </div>

            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left", fontSize: 12 }}>
                <thead>
                  <tr style={{ background: "#f1f5f9", borderBottom: "2px solid #cbd5e1" }}>
                    <th style={{ padding: "8px 10px", color: "#0f172a", fontWeight: 800 }}>Sl</th>
                    <th
                      onClick={() => {
                        if (leaveSortCol === "employee_code") setLeaveSortDir(d => d === "asc" ? "desc" : "asc");
                        else { setLeaveSortCol("employee_code"); setLeaveSortDir("asc"); }
                      }}
                      style={{
                        padding: "8px 10px",
                        color: leaveSortCol === "employee_code" ? "#2563eb" : "#0f172a",
                        fontWeight: 800,
                        cursor: "pointer",
                        userSelect: "none"
                      }}
                      title="Click to sort by Employee ID"
                    >
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                        <span>Employee ID</span>
                        <span style={{ fontSize: 10, color: leaveSortCol === "employee_code" ? "#2563eb" : "#94a3b8" }}>
                          {leaveSortCol === "employee_code" ? (leaveSortDir === "asc" ? "▲" : "▼") : "⇅"}
                        </span>
                      </span>
                    </th>
                    <th
                      onClick={() => {
                        if (leaveSortCol === "name") setLeaveSortDir(d => d === "asc" ? "desc" : "asc");
                        else { setLeaveSortCol("name"); setLeaveSortDir("asc"); }
                      }}
                      style={{
                        padding: "8px 10px",
                        color: leaveSortCol === "name" ? "#2563eb" : "#0f172a",
                        fontWeight: 800,
                        cursor: "pointer",
                        userSelect: "none"
                      }}
                      title="Click to sort by Employee Name"
                    >
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                        <span>Employee Name</span>
                        <span style={{ fontSize: 10, color: leaveSortCol === "name" ? "#2563eb" : "#94a3b8" }}>
                          {leaveSortCol === "name" ? (leaveSortDir === "asc" ? "▲" : "▼") : "⇅"}
                        </span>
                      </span>
                    </th>
                    <th style={{ padding: "8px 10px", color: "#0f172a", fontWeight: 800 }}>Dept</th>
                    <th style={{ padding: "8px 10px", color: "#0f172a", fontWeight: 800 }}>DOJ</th>
                    {leaveTypes.map(lt => (
                      <th key={lt.name} style={{ padding: "8px 10px", color: "#0f172a", fontWeight: 800, textAlign: "center" }}>
                        {lt.name}
                        <div style={{ fontSize: 9.5, color: "#475569" }}>Ent | Used | Bal</div>
                      </th>
                    ))}
                    <th style={{ padding: "8px 10px", color: "#0f172a", fontWeight: 800, textAlign: "center" }}>Total Paid Taken</th>
                    <th style={{ padding: "8px 10px", color: "#0f172a", fontWeight: 800, textAlign: "center" }}>LOP Days</th>
                    <th style={{ padding: "8px 10px", color: "#0f172a", fontWeight: 800, textAlign: "center" }}>Net Closing Bal</th>
                  </tr>
                </thead>
                <tbody>
                  {leaveAuditData.map((item, idx) => (
                    <tr key={item.emp.id} style={{ borderBottom: "1px solid #e2e8f0" }}>
                      <td style={{ padding: "8px 10px", color: "#64748b" }}>{idx + 1}</td>
                      <td style={{ padding: "8px 10px", fontWeight: 700, color: "#0f172a" }}>{item.emp.employee_code || "—"}</td>
                      <td style={{ padding: "8px 10px", fontWeight: 700, color: "#0f172a" }}>{item.emp.name}</td>
                      <td style={{ padding: "8px 10px", color: "#475569" }}>{item.emp.department || "—"}</td>
                      <td style={{ padding: "8px 10px", color: "#64748b", whiteSpace: "nowrap" }}>
                        {item.emp.date_of_joining ? new Date(item.emp.date_of_joining).toLocaleDateString("en-IN") : "—"}
                      </td>

                      {item.metrics.map(m => (
                        <td key={m.name} style={{ padding: "8px 10px", textAlign: "center", whiteSpace: "nowrap" }}>
                          <span>{m.totalQuota}</span>
                          <span style={{ color: "#cbd5e1", margin: "0 3px" }}>/</span>
                          <span style={{ fontWeight: m.approvedUsed > 0 ? 700 : 400 }}>{m.approvedUsed}</span>
                          <span style={{ color: "#cbd5e1", margin: "0 3px" }}>/</span>
                          <strong>{m.remainingBalance}</strong>
                        </td>
                      ))}

                      <td style={{ padding: "8px 10px", textAlign: "center", fontWeight: 700 }}>{item.totalAvailed}</td>
                      <td style={{ padding: "8px 10px", textAlign: "center", color: item.lopDays > 0 ? "#dc2626" : "#64748b", fontWeight: item.lopDays > 0 ? 700 : 400 }}>
                        {item.lopDays || 0}
                      </td>
                      <td style={{ padding: "8px 10px", textAlign: "center", fontWeight: 800, color: "#1d4ed8" }}>
                        {item.totalBalance}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Official Certification & Signature Sign-Off Block */}
            <div style={{ marginTop: 40, paddingTop: 24, borderTop: "2px solid #0f172a" }}>
              <div style={{ fontSize: 11.5, color: "#334155", fontStyle: "italic", marginBottom: 28 }}>
                "Certified that the above leave records for the assessment year {yearFilter} have been examined and compiled in accordance with approved company policy quotas, employee leave requests, and verified attendance punch data."
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 30, textAlign: "center" }}>
                <div>
                  <div style={{ borderBottom: "1px solid #0f172a", height: 40, marginBottom: 8 }}></div>
                  <div style={{ fontSize: 12, fontWeight: 800, color: "#0f172a" }}>Prepared By</div>
                  <div style={{ fontSize: 11, color: "#64748b" }}>HR & Payroll Officer</div>
                </div>
                <div>
                  <div style={{ borderBottom: "1px solid #0f172a", height: 40, marginBottom: 8 }}></div>
                  <div style={{ fontSize: 12, fontWeight: 800, color: "#0f172a" }}>Verified By</div>
                  <div style={{ fontSize: 11, color: "#64748b" }}>Internal / Labor Auditor</div>
                </div>
                <div>
                  <div style={{ borderBottom: "1px solid #0f172a", height: 40, marginBottom: 8 }}></div>
                  <div style={{ fontSize: 12, fontWeight: 800, color: "#0f172a" }}>Authorized Signatory</div>
                  <div style={{ fontSize: 11, color: "#64748b" }}>For {company.name}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ───────────────────────────────────────────────────────────────────────
          TAB 6: MONTHLY PAYROLL REGISTER REPORT
      ─────────────────────────────────────────────────────────────────────── */}
      {activeTab === "payroll" && (
        <div>
          {/* KPI Summary Strip */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14, marginBottom: 20 }}>
            <div className="rep-kpi-card">
              <div style={{ fontSize: 12, fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>Processed Employees</div>
              <div style={{ fontSize: 24, fontWeight: 800, color: "#1d4ed8", marginTop: 4 }}>{payrollMonthData.length}</div>
              <div style={{ fontSize: 11.5, color: "#94a3b8", marginTop: 2 }}>For {monthFilter}/{yearFilter}</div>
            </div>
            <div className="rep-kpi-card">
              <div style={{ fontSize: 12, fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>Total Gross Earnings</div>
              <div style={{ fontSize: 24, fontWeight: 800, color: "#0f766e", marginTop: 4 }}>
                ₹{Math.round(payrollMonthData.reduce((acc, s) => acc + (s.gross || 0), 0)).toLocaleString("en-IN")}
              </div>
              <div style={{ fontSize: 11.5, color: "#94a3b8", marginTop: 2 }}>Basic + Allowances</div>
            </div>
            <div className="rep-kpi-card">
              <div style={{ fontSize: 12, fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>Total Net Payout</div>
              <div style={{ fontSize: 24, fontWeight: 800, color: "#2563eb", marginTop: 4 }}>
                ₹{Math.round(payrollMonthData.reduce((acc, s) => acc + (s.net_pay || 0), 0)).toLocaleString("en-IN")}
              </div>
              <div style={{ fontSize: 11.5, color: "#94a3b8", marginTop: 2 }}>Net Bank Transfer Due</div>
            </div>
            <div className="rep-kpi-card">
              <div style={{ fontSize: 12, fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>Statutory Remittances</div>
              <div style={{ fontSize: 24, fontWeight: 800, color: "#7e22ce", marginTop: 4 }}>
                ₹{Math.round(payrollMonthData.reduce((acc, s) => acc + (s.pf_employee || 0) + (s.esi_employee || 0) + (s.professional_tax || 0), 0)).toLocaleString("en-IN")}
              </div>
              <div style={{ fontSize: 11.5, color: "#94a3b8", marginTop: 2 }}>EPF + ESI + PT Dues</div>
            </div>
          </div>

          <div className="rep-card" style={{ padding: "20px 24px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: 16, color: "#0f172a" }}>💰 Monthly Payroll & Salary Register</div>
                <div style={{ color: "#64748b", fontSize: 12.5, marginTop: 2 }}>
                  Month: {monthFilter} / {yearFilter} • Master payout register with earning components and statutory deductions.
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                {onDownloadMasterExcel && (
                  <button
                    className="rep-btn-primary"
                    onClick={() => onDownloadMasterExcel(monthFilter, yearFilter)}
                    style={{ padding: "7px 14px", fontSize: 12.5 }}
                    title="Download 4-sheet formula-driven Master Excel (Payroll Master, ESI, PF, Executive Summary)"
                  >
                    <span>📊</span> Master Excel (4 Sheets with Formulas)
                  </button>
                )}
                <button
                  className="rep-btn-primary"
                  onClick={handleExportPayrollRegister}
                  style={{ padding: "7px 14px", fontSize: 12.5 }}
                  title="Quick export of current table view to Excel"
                >
                  <span>📥</span> Quick Table Excel
                </button>
              </div>
            </div>

            {loadingPayroll ? (
              <div style={{ textAlign: "center", padding: 40, color: "#64748b" }}>Loading payroll data...</div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left", fontSize: 12.5 }}>
                  <thead>
                    <tr style={{ background: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                      <th
                        onClick={() => {
                          if (payrollSortCol === "employee_code") setPayrollSortDir(d => d === "asc" ? "desc" : "asc");
                          else { setPayrollSortCol("employee_code"); setPayrollSortDir("asc"); }
                        }}
                        style={{
                          padding: "12px 14px",
                          color: payrollSortCol === "employee_code" ? "#2563eb" : "#334155",
                          fontWeight: 700,
                          cursor: "pointer",
                          userSelect: "none"
                        }}
                        title="Click to sort by Employee ID"
                      >
                        <div style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                          <span>Employee ID</span>
                          <span style={{ fontSize: 11, color: payrollSortCol === "employee_code" ? "#2563eb" : "#94a3b8" }}>
                            {payrollSortCol === "employee_code" ? (payrollSortDir === "asc" ? "▲" : "▼") : "⇅"}
                          </span>
                        </div>
                      </th>
                      <th
                        onClick={() => {
                          if (payrollSortCol === "name") setPayrollSortDir(d => d === "asc" ? "desc" : "asc");
                          else { setPayrollSortCol("name"); setPayrollSortDir("asc"); }
                        }}
                        style={{
                          padding: "12px 14px",
                          color: payrollSortCol === "name" ? "#2563eb" : "#334155",
                          fontWeight: 700,
                          cursor: "pointer",
                          userSelect: "none"
                        }}
                        title="Click to sort by Employee Name"
                      >
                        <div style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                          <span>Employee Name</span>
                          <span style={{ fontSize: 11, color: payrollSortCol === "name" ? "#2563eb" : "#94a3b8" }}>
                            {payrollSortCol === "name" ? (payrollSortDir === "asc" ? "▲" : "▼") : "⇅"}
                          </span>
                        </div>
                      </th>
                      <th style={{ padding: "12px 14px", color: "#334155", fontWeight: 700 }}>Department</th>
                      <th style={{ padding: "12px 14px", color: "#334155", fontWeight: 700 }}>Bank & A/C</th>
                      <th style={{ padding: "12px 14px", color: "#334155", fontWeight: 700, textAlign: "right" }}>Basic</th>
                      <th style={{ padding: "12px 14px", color: "#334155", fontWeight: 700, textAlign: "right" }}>HRA</th>
                      <th style={{ padding: "12px 14px", color: "#334155", fontWeight: 700, textAlign: "right" }}>Special</th>
                      <th style={{ padding: "12px 14px", color: "#334155", fontWeight: 700, textAlign: "right" }}>Gross</th>
                      <th style={{ padding: "12px 14px", color: "#334155", fontWeight: 700, textAlign: "right" }}>PF (Emp)</th>
                      <th style={{ padding: "12px 14px", color: "#334155", fontWeight: 700, textAlign: "right" }}>ESI (Emp)</th>
                      <th style={{ padding: "12px 14px", color: "#334155", fontWeight: 700, textAlign: "right" }}>PT</th>
                      <th style={{ padding: "12px 14px", color: "#334155", fontWeight: 700, textAlign: "right" }}>Net Pay</th>
                      <th style={{ padding: "12px 14px", color: "#334155", fontWeight: 700, textAlign: "center" }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedPayrollData.length === 0 ? (
                      <tr>
                        <td colSpan={13} style={{ textAlign: "center", padding: 32, color: "#94a3b8" }}>
                          No processed payroll records found for {monthFilter}/{yearFilter}.
                        </td>
                      </tr>
                    ) : (
                      sortedPayrollData.map(sal => (
                        <tr key={sal.id} className="rep-table-row" style={{ borderBottom: "1px solid #f1f5f9" }}>
                          <td style={{ padding: "11px 14px", fontWeight: 700, color: "#1d4ed8" }}>
                            {sal.employees?.employee_code || "—"}
                          </td>
                          <td style={{ padding: "11px 14px", fontWeight: 700, color: "#0f172a" }}>
                            {sal.employees?.name}
                          </td>
                          <td style={{ padding: "11px 14px", color: "#64748b" }}>{sal.employees?.department || "—"}</td>
                          <td style={{ padding: "11px 14px", color: "#475569", fontSize: 11.5 }}>
                            <div>{sal.employees?.bank_name || "—"}</div>
                            <div style={{ color: "#94a3b8" }}>{sal.employees?.account_number ? `...${String(sal.employees.account_number).slice(-4)}` : ""}</div>
                          </td>
                          <td style={{ padding: "11px 14px", textAlign: "right", color: "#334155" }}>₹{Math.round(Number(sal.basic || 0)).toLocaleString("en-IN")}</td>
                          <td style={{ padding: "11px 14px", textAlign: "right", color: "#334155" }}>₹{Math.round(Number(sal.hra || 0)).toLocaleString("en-IN")}</td>
                          <td style={{ padding: "11px 14px", textAlign: "right", color: "#334155" }}>₹{Math.round(Number(sal.special_allowance || 0)).toLocaleString("en-IN")}</td>
                          <td style={{ padding: "11px 14px", textAlign: "right", fontWeight: 700, color: "#0f766e" }}>₹{Math.round(Number(sal.gross || 0)).toLocaleString("en-IN")}</td>
                          <td style={{ padding: "11px 14px", textAlign: "right", color: "#64748b" }}>₹{Math.round(Number(sal.pf_employee || 0)).toLocaleString("en-IN")}</td>
                          <td style={{ padding: "11px 14px", textAlign: "right", color: "#64748b" }}>₹{Math.round(Number(sal.esi_employee || 0)).toLocaleString("en-IN")}</td>
                          <td style={{ padding: "11px 14px", textAlign: "right", color: "#64748b" }}>₹{Math.round(Number(sal.professional_tax || 0)).toLocaleString("en-IN")}</td>
                          <td style={{ padding: "11px 14px", textAlign: "right", fontWeight: 800, color: "#1d4ed8" }}>₹{Math.round(Number(sal.net_pay || 0)).toLocaleString("en-IN")}</td>
                          <td style={{ padding: "11px 14px", textAlign: "center" }}>
                            <span style={{
                              display: "inline-block", padding: "2px 8px", borderRadius: 12, fontSize: 11, fontWeight: 700,
                              background: sal.status === "finalized" ? "#eff6ff" : "#f1f5f9",
                              color: sal.status === "finalized" ? "#1d4ed8" : "#64748b",
                            }}>
                              {(sal.status || "draft").toUpperCase()}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function activeCompanyBadge(company) {
  if (!company) return "Reports Hub";
  return `${company.name}`;
}
